"use client";

import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherMail,
  FeatherMoreHorizontal,
  FeatherRefreshCw,
  FeatherTrash2,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import {
  getListInvitationsQueryControllerListQueryKey,
  useCreateInvitationCommandControllerCreate,
} from "@/generated/invitations/invitations";
import {
  CreateInvitationDtoRole,
  InvitationItemDto,
  InvitationItemDtoStatus,
} from "@/generated/model";

interface InvitationsTabProps {
  organisationSlug: string;
  data: InvitationItemDto[];
  isLoading: boolean;
  error: unknown;
}

export function InvitationsTab({
  organisationSlug,
  data: invitations,
  isLoading,
  error,
}: InvitationsTabProps) {
  const queryClient = useQueryClient();
  const [revokeDialog, setRevokeDialog] = useState<{
    open: boolean;
    invitation: InvitationItemDto | null;
  }>({ open: false, invitation: null });
  const [isProcessing, setIsProcessing] = useState(false);

  const { mutate: resendInvitation } =
    useCreateInvitationCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Invitation resent successfully");
          await queryClient.invalidateQueries({
            queryKey: getListInvitationsQueryControllerListQueryKey(
              organisationSlug,
              {},
            ),
          });
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to resend invitation";
          toast.error(errorMessage);
        },
      },
    });

  const handleResend = (invitation: InvitationItemDto) => {
    resendInvitation({
      organisationSlug,
      data: {
        email: invitation.email,
        role: invitation.roleOffered as unknown as CreateInvitationDtoRole,
      },
    });
  };

  const handleRevoke = () => {
    if (!revokeDialog.invitation) return;

    // TODO: Implement when API supports revoke
    setIsProcessing(true);
    setTimeout(() => {
      toast.info("Revoke functionality coming soon");
      setRevokeDialog({ open: false, invitation: null });
      setIsProcessing(false);
    }, 500);
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
        <IconWithLogoBackground variant="error" size="x-large" />
        <div className="flex flex-col items-center gap-2">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Error loading invitations
          </span>
          <span className="text-body font-body text-subtext-color">
            Please try again later.
          </span>
        </div>
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className="flex w-full flex-col items-center justify-center py-12 gap-4 rounded-md border border-solid border-secondary-beige-200">
        <IconWithLogoBackground
          variant="neutral"
          size="x-large"
          icon={<FeatherMail />}
        />
        <div className="flex flex-col items-center gap-2">
          <span className="text-heading-3 font-heading-3 text-default-font">
            No pending invitations
          </span>
          <span className="text-body font-body text-subtext-color text-center max-w-md">
            Send an invitation to add new members to your nursery group.
          </span>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: InvitationItemDtoStatus) => {
    switch (status) {
      case InvitationItemDtoStatus.PENDING:
        return <Badge variant="warning">Pending</Badge>;
      case InvitationItemDtoStatus.ACCEPTED:
        return <Badge variant="success">Accepted</Badge>;
      case InvitationItemDtoStatus.EXPIRED:
        return <Badge variant="neutral">Expired</Badge>;
      case InvitationItemDtoStatus.REVOKED:
        return <Badge variant="error">Revoked</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  return (
    <>
      <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
        <Table
          className={TABLE_MIN_WIDTH.md}
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Role</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Invited</Table.HeaderCell>
              <Table.HeaderCell className="items-center justify-end">
                Actions
              </Table.HeaderCell>
            </Table.HeaderRow>
          }
        >
          {invitations.map((invitation) => {
            const canTakeAction =
              invitation.status !== InvitationItemDtoStatus.ACCEPTED &&
              invitation.status !== InvitationItemDtoStatus.REVOKED;

            return (
              <Table.Row key={invitation.id}>
                <Table.Cell>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {invitation.email}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-default-font capitalize">
                    {invitation.roleOffered === "ADMIN"
                      ? "Group Administrator"
                      : "Member"}
                  </span>
                </Table.Cell>
                <Table.Cell>{getStatusBadge(invitation.status)}</Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-subtext-color">
                    {new Date(invitation.createdAt).toLocaleDateString()}
                  </span>
                </Table.Cell>
                <Table.Cell variant="aligned-right">
                  {canTakeAction && (
                    <div className="flex items-center justify-end gap-2">
                      <SubframeCore.DropdownMenu.Root>
                        <SubframeCore.DropdownMenu.Trigger asChild={true}>
                          <IconButton
                            variant="neutral-primary"
                            size="medium"
                            icon={<FeatherMoreHorizontal />}
                          />
                        </SubframeCore.DropdownMenu.Trigger>
                        <SubframeCore.DropdownMenu.Portal>
                          <SubframeCore.DropdownMenu.Content
                            side="bottom"
                            align="end"
                            sideOffset={4}
                            asChild={true}
                          >
                            <DropdownMenu>
                              <DropdownMenu.DropdownItem
                                icon={<FeatherRefreshCw />}
                                onClick={() => handleResend(invitation)}
                              >
                                Resend
                              </DropdownMenu.DropdownItem>
                              {/* <DropdownMenu.DropdownItem
                                icon={<FeatherX />}
                                onClick={() =>
                                  setRevokeDialog({
                                    open: true,
                                    invitation,
                                  })
                                }
                              >
                                Revoke
                              </DropdownMenu.DropdownItem> */}
                            </DropdownMenu>
                          </SubframeCore.DropdownMenu.Content>
                        </SubframeCore.DropdownMenu.Portal>
                      </SubframeCore.DropdownMenu.Root>
                    </div>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table>
      </div>

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={revokeDialog.open}
        onOpenChange={(open) => setRevokeDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <DialogTitle className="sr-only">Revoke invitation</DialogTitle>
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
              <div className="flex flex-col items-center gap-6">
                <IconWithLogoBackground
                  variant="error"
                  size="x-large"
                  icon={<FeatherTrash2 />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center">
                    Revoke invitation?
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    Are you sure you want to revoke the invitation for{" "}
                    <strong>{revokeDialog.invitation?.email}</strong>? They will
                    no longer be able to join the nursery group using this
                    invitation.
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
              <Button
                className="h-14 w-80 flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={() =>
                  setRevokeDialog({ open: false, invitation: null })
                }
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                className="h-14 w-80 flex-none"
                variant="destructive-primary"
                size="extra-large"
                onClick={handleRevoke}
                disabled={isProcessing}
              >
                {isProcessing ? "Revoking..." : "Yes, revoke"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
